import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { Session } from '@supabase/supabase-js';
import { supabase } from '../lib/supabaseClient';
import { clearCache } from '../api/cache';

/**
 * Real Supabase Auth session plumbing: session restore on load, live
 * updates via onAuthStateChange (covers login, logout, and token refresh),
 * sign-in, and sign-out. This is the ONLY place in the app that talks to
 * `supabase.auth` directly — every other module either reads `useAuth()`
 * or, for data, relies on the JWT already being attached to every
 * `supabase.rpc()`/`.from()` call automatically by supabase-js.
 *
 * STATUS: IMPLEMENTED_UNVERIFIED — see docs/data-dictionary.md §8k. Written
 * against the real `@supabase/supabase-js` v2 Auth API; this sandbox cannot
 * install that package to execute it (npm registry blocked).
 */

interface AuthState {
  session: Session | null;
  loading: boolean;
  error: string | null;
  signIn: (email: string, password: string) => Promise<{ error: string | null }>;
  signOut: () => Promise<void>;
  /**
   * Always resolves successfully (never rejects, never reports whether the
   * email is registered) — this is the anti-enumeration contract. The real
   * `supabase.auth.resetPasswordForEmail` call itself never tells the caller
   * whether the account exists either (Supabase Auth's documented behavior),
   * so the UI showing a single generic message regardless of outcome is not
   * a workaround, it's just not inventing a branch that isn't there.
   */
  requestPasswordReset: (email: string) => Promise<void>;
  /**
   * Completes a reset: only valid once Supabase Auth has already exchanged
   * the emailed recovery link for a temporary session (it does this itself
   * via `detectSessionInUrl` before this is ever called) — this just sets
   * the new password on that session.
   */
  updatePassword: (newPassword: string) => Promise<{ error: string | null }>;
}

export const AuthContext = createContext<AuthState | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    // Session restore: supabase-js already rehydrated any persisted session
    // from localStorage synchronously into its internal state; getSession()
    // returns it (or null) without a network round-trip in the common case.
    supabase.auth.getSession().then(({ data }) => {
      if (!cancelled) {
        setSession(data.session);
        setLoading(false);
      }
    });

    // Live updates: fires on SIGNED_IN, SIGNED_OUT, TOKEN_REFRESHED, and
    // USER_UPDATED — this is what makes logout (in any tab) and silent
    // token refresh propagate through the app without a manual poll.
    const { data: listener } = supabase.auth.onAuthStateChange((_event, newSession) => {
      setSession(newSession);
      setLoading(false);
    });

    return () => {
      cancelled = true;
      listener.subscription.unsubscribe();
    };
  }, []);

  const signIn = async (email: string, password: string): Promise<{ error: string | null }> => {
    setError(null);
    const { error: signInError } = await supabase.auth.signInWithPassword({ email, password });
    if (signInError) {
      setError(signInError.message);
      return { error: signInError.message };
    }
    return { error: null };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    // Never let a cached reference-data row from this session answer a
    // request made by whoever signs in next in this same browser tab.
    clearCache();
    // onAuthStateChange fires SIGNED_OUT and clears `session` above; no
    // need to setSession(null) here too.
  };

  const requestPasswordReset = async (email: string): Promise<void> => {
    // Deliberately ignore the resolved error: whether the email is
    // registered must never change what the caller (and therefore the UI)
    // observes. supabase-js resolves this call the same way for both a
    // real, registered email and an unregistered one.
    try {
      await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
    } catch {
      // Swallow network/config errors too — same reasoning as above; a
      // generic "check your email" message is shown either way.
    }
  };

  const updatePassword = async (newPassword: string): Promise<{ error: string | null }> => {
    const { error: updateError } = await supabase.auth.updateUser({ password: newPassword });
    return { error: updateError ? updateError.message : null };
  };

  const value = useMemo<AuthState>(
    () => ({ session, loading, error, signIn, signOut, requestPasswordReset, updatePassword }),
    [session, loading, error]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth() must be called within <AuthProvider>');
  }
  return ctx;
}
